
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Bus, Clock, Zap } from "lucide-react";
import BusAnimationOnGrid from "./heroCompounent/AnimatedRoutes";
import { useTheme } from "../contexts/ThemeContext";

const featureCards = [
  {
    icon: <Clock className="text-amber-400" size={39} />,
    title: "Save Time",
    desc: "Reduce travel time by up to 35% with efficient scheduling.",
  },
  {
    icon: <Zap className="text-amber-400" size={39} />,
    title: "Smart Routing",
    desc: "AI-powered routes that avoid delays and bottlenecks.",
  },
  {
    icon: <Bus className="text-amber-400" size={39} />,
    title: "Live Tracking",
    desc: "Track your bus in real-time with GPS accuracy.",
  },
];

const Hero: React.FC = React.memo(() => {
  const [scrollY, setScrollY] = useState(0);
  const { resolvedTheme } = useTheme();
  /* The wave divider must match the colour of the section right BELOW it
     (Features section, which uses bg-gray-50 / dark:!bg-ink-850). */
  const waveFill = resolvedTheme === "dark" ? "#13161e" : "#F9FAFB";

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <section
        className="relative min-h-screen overflow-hidden pt-28 sm:pt-32"
        id="home"
      >
        {/* Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-800 opacity-90 z-0"></div>
        <div
          className="absolute inset-0 bg-[url('https://wallpaperaccess.com/full/1628619.jpg')] bg-center bg-no-repeat z-0"
          style={{ backgroundSize: 'cover' }}
        ></div>
        <div className="absolute inset-0 bg-gray-900 opacity-70 z-0"></div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            {/* Text Section */}
            <motion.div
              className="w-full lg:w-1/2 text-center lg:text-left text-white"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.7rem] font-serif font-bold mb-6 leading-tight">
                AI-Powered <span className="text-amber-400">Transport</span> For
                Your Smart Journey
              </h1>
              <p className="text-sm sm:text-[15px] md:text-base mb-6 md:pr-16 px-3 sm:px-0 text-gray-200 leading-relaxed">
                Revolutionizing the way you travel with intelligent route
                optimization and real-time tracking.
              </p>

              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                <button
                  onClick={() => { window.location.href = '/login'; }}
                  className="text-sm sm:text-[14.5px] bg-amber-600 hover:bg-amber-500 text-white px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Get Started
                </button>
                <button className="text-sm sm:text-[15.3px] border-2 border-white hover:bg-white hover:text-primary-500 text-white px-6 py-3 rounded-full font-medium transition-all duration-300">
                  Learn More
                </button>
              </div>
            </motion.div>

            {/* Animation Section */}
            <motion.div
              className="w-full lg:w-1/2 flex justify-center items-center overflow-hidden"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="w-full max-w-[320px] sm:max-w-[380px] md:max-w-[450px] lg:max-w-[520px] xl:max-w-full px-2 sm:px-4 md:px-2 lg:px-0">
                <div className="transform scale-90 sm:scale-95 md:scale-100 origin-center">
                  <BusAnimationOnGrid />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Wave Divider: curved on laptop (lg+) and straight on mobile */}
        <div className="absolute bottom-0 left-0 w-full pointer-events-none z-20">
          {/* Straight edge on mobile/tablet */}
          {/* <div className="block lg:hidden">
            <div className="w-full h-12 sm:h-14 md:h-16 bg-white"></div>
          </div> */}

          {/* Curved wave on laptop and larger - reduced depth */}
          <div className="hidden lg:block -mb-[80px]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1440 260"
              fill={waveFill}
            >
              <path
                fillOpacity="1"
                d="M0,80L48,96C96,112,192,144,288,144C384,144,480,120,576,125.3C672,130,768,161,864,150.7C960,140,1056,96,1152,82.7C1248,69,1344,85,1392,93.3L1440,100L1440,260L1392,260C1344,260,1248,260,1152,260C1056,260,960,260,864,260C768,260,672,260,576,260C480,260,384,260,288,260C192,260,96,260,48,260L0,260Z"
              />
            </svg>
          </div>
        </div>
      </section>

      {/* TRANSPARENT FEATURE CARDS SECTION  (Combine)
<section className="relative -mt-32 z-30 px-6 md:px-20 pb-24">
  <motion.div
    className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto bg-white/10 rounded-2xl p-8 shadow-xl"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    {[
      {
        icon: <Clock className="text-amber-400" size={40} />,
        title: 'Save Time',
        desc: 'Reduce travel time by up to 35% with efficient scheduling.',
      },
      {
        icon: <Zap className="text-amber-400" size={40} />,
        title: 'Smart Routing',
        desc: 'AI-powered routes that avoid delays and bottlenecks.',
      },
      {
        icon: <Bus className="text-amber-400" size={40} />,
        title: 'Live Tracking',
        desc: 'Track your bus in real-time with GPS accuracy.',
      },
    ].map((item, i) => (
      <motion.div
        key={i}
        className="flex flex-col items-center rounded-2xl p-8 transition-all duration-300 hover:scale-105"
        whileHover={{ y: -5 }}
        transition={{ duration: 0.2 }}
      >
        <div className="mb-4 p-3 bg-transparent rounded-full">
          {item.icon}
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-3">{item.title}</h3>
        <p className="text-gray-800 text-center leading-relaxed">{item.desc}</p>
      </motion.div>
    ))}
  </motion.div>
</section>
*/}
    </>
  );
});

Hero.displayName = 'Hero';
export default Hero;

